import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { AiOutlineWarning } from "react-icons/ai";

import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import Checkbox from "../../../../../../../components/Form/Checkbox";
import Modal from "../../../../../../../components/Generic/Modal";
import { Job } from "../../../../../../../models/job";
import {
  OrdersOptions,
  UnloadModalWrapper,
  WarningIconContainer,
  WarningText,
  WarningTextContainer,
  WarningTitle,
  WarningWrapper,
} from "./styles";

interface UnloadOrdersModalProps {
  open: boolean;
  closeModal: () => void;
  hasError?: boolean;
  isLoading?: boolean;
  unloadStorageBox?: (orders: Job[]) => void;
  storageBoxAndUnitOrders?: Job[];
}

export const UnloadOrdersModal = ({
  open,
  closeModal,
  isLoading,
  hasError,
  unloadStorageBox,
  storageBoxAndUnitOrders,
}: UnloadOrdersModalProps) => {
  const { t: translate } = useTranslation();

  const [selectedOrdersToUnload, setSelectedOrdersToUnload] = useState<Job[]>([]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      closeModal();
    }
  }, [hasError]);

  const handleUnload = () => {
    if (selectedOrdersToUnload.length && selectedOrdersToUnload.length > 0) {
      unloadStorageBox?.(selectedOrdersToUnload);
    }
  };

  const getOrderReferenceAndCustomerName = (order: Job) => {
    return `${order?.reference || ""}${order?.sequence ? `/${order?.sequence}` : ""} - ${
      order?.customer?.name || ""
    } ${order?.customer?.lastName || ""}`;
  };

  return (
    <Modal
      open={open}
      handleClose={() => {
        closeModal();
      }}
      title={translate("selectWhichOrderToUnload")}
      disableEnforceFocus
      data-cy="unloadStorageBoxAndUnitModal"
      dataCyTitle="unloadStorageBoxAndUnitModalTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          closeModal();
        },
        dataCySave: "unloadButton",
        textSave: translate("unload"),
        loading: isLoading,
        saveDisabled: !(selectedOrdersToUnload.length > 0) || isLoading,
        onSave: () => handleUnload(),
      }}
    >
      <UnloadModalWrapper container direction="row" alignItems="flex-start">
        <AutoSuggest
          label={translate("orders")}
          placeholder={translate("select")}
          dataCy="storageBoxAndUnitOrders"
          options={storageBoxAndUnitOrders}
          getOptionLabel={(option: Job) => `#${getOrderReferenceAndCustomerName(option)}`}
          getOptionSelected={(option: Job, value: Job) => {
            return option.id === value.id;
          }}
          value={selectedOrdersToUnload}
          renderOption={(
            props: React.HTMLAttributes<any>,
            option: Job,
            { selected }: { selected: boolean }
          ) => (
            <OrdersOptions {...props}>
              <Checkbox checked={selected} />
              <span>{getOrderReferenceAndCustomerName(option)}</span>
            </OrdersOptions>
          )}
          multiple
          onChange={(options: Job[]) => {
            setSelectedOrdersToUnload(options);
          }}
        />
        {selectedOrdersToUnload.length > 0 && (
          <WarningWrapper container direction="row">
            <WarningIconContainer>
              <AiOutlineWarning size={18} color={"#AA5103"} />
            </WarningIconContainer>
            <WarningTextContainer>
              <WarningTitle>{translate("pleasePayAttention")}</WarningTitle>
              <WarningText>
                {translate("onceTheOrderIsUnloadedItCannotChangeStatusAgain")}
              </WarningText>
            </WarningTextContainer>
          </WarningWrapper>
        )}
      </UnloadModalWrapper>
    </Modal>
  );
};
